import Box from '@mui/material/Box';
import escape from 'regexp.escape';

// regexp.escape is a shim, may be removed in the future, if there is wide browser adoption

/**
 * Renders text with highlighted segments matching the given phrase.
 *
 * @param text - The input text to search within.
 * @param phrase - The phrase to highlight.
 * @param normalizer - Function to normalize text and phrase before matching. Defaults to lowercase conversion.
 * @param regexpSearch - Optional regex pattern string for custom splitting of the phrase.
 * @returns The React nodes with highlighted matching parts.
 */
export const HighlightText = ({
	text,
	phrase,
	normalizer = (s) => s.trim().toLowerCase(),
	regexpSearch,
}: {
	text: string | null;
	phrase: string | null;
	normalizer?: (s: string) => string;
	regexpSearch?: string;
}): React.ReactNode | string => {
	if (!phrase || !text) {
		return text;
	}
	const normalizedPhrase = normalizer(phrase);
	const localRegexpSearch = regexpSearch
		? regexpSearch
		: `(${escape(normalizedPhrase)})`;
	const regex = new RegExp(localRegexpSearch, 'gi');
	const parts = text.split(regex);

	return parts.map((part) =>
		normalizer(part) === normalizedPhrase ? (
			<Box
				component="mark"
				key={part}
				className="highlighted-text"
				sx={{ bgcolor: 'blue.200' }}>
				{part}
			</Box>
		) : (
			part
		)
	);
};

export const alphaNumericLowerCaseNormalizer = (s: string): string =>
	s.replace(/[^a-z0-9]/gi, '').toLowerCase();

/**
 * This regexp function will match any non-alphanumerical characters in between the characters of the phrase
 * which in turn will allow us to highlight the phrase in the text,
 * the same as the query will work in database, as it strips all the non-alphanumeric characters
 * the trick is to split the phrase into characters and join them with '[^a-z0-9]*?' to create a regexp search
 */
export const getIdentifiersRegexpSearch = (phrase: string) =>
	`(${alphaNumericLowerCaseNormalizer(phrase).split('').join('[^a-z0-9]*?')})`;
